import React, { useEffect, useRef } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ChartBarIcon,
  CodeBracketIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import { SEMHR_TOUR } from './semanticHrTourData';
import { AttritionLineChartSvg, HeatmapSvg } from './semanticHrTourCharts';

/** ready = send, loading = generating, stop = cancel in-flight request */
export function QuerySubmitButton({ state = 'ready', size = 'sm', className = '' }) {
  const dim = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  const iconClass = size === 'md' ? 'h-3 w-3' : 'h-2.5 w-2.5';
  const bg =
    state === 'loading' ? 'bg-brand-500 shadow-sm shadow-brand-500/30'
      : state === 'stop' ? 'bg-rose-500 shadow-sm shadow-rose-500/25'
        : 'bg-ink-800';

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-white transition-colors duration-300 ${dim} ${bg} ${className}`}
      aria-hidden="true"
    >
      {state === 'loading' ? (
        <span className={`${iconClass} animate-spin rounded-full border-2 border-white/25 border-t-white`} />
      ) : state === 'stop' ? (
        <StopIcon className={iconClass} />
      ) : (
        <ArrowUpIcon className={iconClass} strokeWidth={2.5} />
      )}
    </span>
  );
}

function resolveDynamicQuerySubmitState(phase) {
  if (phase === 'empty') return 'ready';
  if (phase === 'prompt') return 'loading';
  if (phase === 'heatmap' || phase === 'heatmap-edit') return 'stop';
  return 'ready';
}

function resolveEditSubmitState(phase) {
  if (phase === 'heatmap-edit') return 'loading';
  if (phase === 'line') return 'ready';
  return 'ready';
}

export function DynamicVizFooterStrip() {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-ink-200/80 bg-[#f8fafc] px-2 py-1 text-[7px]">
      <span className="font-semibold text-ink-800">Dynamic Visualization</span>
      <span className="rounded bg-brand-100 px-1 py-px text-[7px] font-bold uppercase tracking-wide text-brand-700">Cortex Analyst</span>
      <span className="text-ink-400">Optional. Charts in chat stay in the thread.</span>
    </div>
  );
}

function ChartCardChrome({ title, subtitle }) {
  return (
    <div className="flex items-center justify-between gap-1 border-b border-ink-100 bg-[#f8fafc] px-2 py-1">
      <div className="min-w-0">
        <p className="truncate text-[8px] font-semibold text-[#1e4d8c]">{title}</p>
        {subtitle ? <p className="text-[7px] text-ink-500">{subtitle}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-0.5 text-ink-500">
        <span className="inline-flex items-center gap-0.5 rounded border border-brand-200/80 bg-brand-50 px-1 py-px text-[7px] font-medium text-brand-700">
          <ChartBarIcon className="h-2 w-2" /> Chart
        </span>
        <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 px-1 py-px text-[7px] font-medium text-ink-500">
          <CodeBracketIcon className="h-2 w-2" /> SQL
        </span>
      </div>
    </div>
  );
}

export default function DynamicVisualizationPanel({ phase, embedded = false, docked = false, stacked = false }) {
  const innerScrollRef = useRef(null);
  const isShell = phase === 'shell';
  const showEmpty = phase === 'empty';
  const showPromptBox = isShell || phase === 'empty' || phase === 'prompt' || phase === 'heatmap' || phase === 'heatmap-edit' || phase === 'line';
  const showSubmittedPrompt = phase === 'prompt' || phase === 'heatmap' || phase === 'heatmap-edit' || phase === 'line';
  const showPromptExtras = phase === 'empty' && !showSubmittedPrompt;
  const showHeatmap = phase === 'heatmap' || phase === 'heatmap-edit';
  const showEditSection = phase === 'heatmap-edit' || phase === 'line';
  const showLine = phase === 'line';

  useEffect(() => {
    if (embedded || docked || stacked) return undefined;
    const el = innerScrollRef.current;
    if (!el) return undefined;
    const frame = requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    return () => cancelAnimationFrame(frame);
  }, [phase, embedded, docked, stacked]);

  const promptText = showSubmittedPrompt ? SEMHR_TOUR.dynamicPrompt : SEMHR_TOUR.dynamicPlaceholder;

  const shellClass = stacked
    ? 'ia-semhr-dv-stacked w-full border-t border-ink-200/80 bg-[#f4f7fb]'
    : docked
      ? 'flex max-h-[42%] min-h-[96px] shrink-0 flex-col overflow-hidden border-t border-ink-200/80 bg-[#f4f7fb]'
      : embedded
        ? 'overflow-hidden rounded-sm border border-ink-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.06)] bg-[#f4f7fb]'
        : 'flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f4f7fb]';

  const bodyClass = stacked
    ? 'ia-semhr-dv-stacked-body px-2 py-1.5'
    : docked
      ? 'ia-semhr-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-1.5'
      : embedded
        ? 'px-2 py-1.5'
        : 'ia-semhr-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-1.5';

  return (
    <div className={shellClass}>
      <div className="shrink-0 border-b border-ink-200/80 bg-white px-2 py-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-[8px] font-semibold text-ink-800">Dynamic Visualization</span>
          <span className="rounded bg-brand-100 px-1 py-px text-[7px] font-bold uppercase tracking-wide text-brand-700">Cortex Analyst</span>
        </div>
        <p className="mt-0.5 text-[7px] text-ink-400">Optional. Charts in chat stay in the thread.</p>
      </div>

      <div
        ref={embedded ? null : innerScrollRef}
        className={bodyClass}
      >
        {showPromptBox && (
          <div className={`overflow-hidden rounded-lg border border-ink-200/90 bg-white shadow-sm ${stacked && showPromptExtras ? 'ia-semhr-dv-query-shell' : ''}`}>
            <div className="flex min-h-[24px] items-center gap-1 px-2 py-1">
              <p className={`min-w-0 flex-1 text-[7px] leading-snug ${showSubmittedPrompt ? 'text-ink-800' : 'text-ink-400'}`}>
                {promptText}
              </p>
              <QuerySubmitButton state={showSubmittedPrompt ? resolveDynamicQuerySubmitState(phase) : 'ready'} />
            </div>
            <div className="flex flex-wrap items-center gap-1 border-t border-ink-100 bg-[#f8fafc] px-2 py-0.5">
              <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 bg-white px-1.5 py-px text-[7px] text-ink-600">
                <ChartBarIcon className="h-2.5 w-2.5" /> Chart type ▾
              </span>
            </div>
            {showPromptExtras && (
              <>
                <div className="border-t border-ink-100 px-2 py-0.5">
                  <p className="text-[7px] font-semibold uppercase tracking-wide text-ink-400">Suggested prompts</p>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {SEMHR_TOUR.dynamicSuggestions.map((s) => (
                      <span key={s} className="rounded-full border border-ink-200/90 bg-[#f0f6ff] px-1.5 py-px text-[7px] text-ink-600">{s}</span>
                    ))}
                  </div>
                </div>
                <p className="border-t border-ink-100 px-2 py-0.5 text-[7px] leading-snug text-ink-400">
                  Charts from your chat answers appear in the conversation. Enter a prompt here to run a separate Cortex Analyst visualization.
                </p>
              </>
            )}
          </div>
        )}

        {(showEmpty || phase === 'prompt') && !showHeatmap && (
          <div className={`ia-semhr-dv-viz-reserve mt-1.5 flex items-center justify-center rounded-lg border border-dashed border-[#b8ccf0]/80 bg-[#eef4ff]/60 ${showEmpty ? '' : 'border-transparent bg-transparent'}`}>
            {showEmpty ? <p className="text-[7px] text-ink-400">Visualization output will appear here</p> : null}
          </div>
        )}

        {showHeatmap && (
          <div className="ia-semhr-heatmap-card mt-1 overflow-hidden rounded-lg border border-ink-200/90 bg-white shadow-sm">
            <ChartCardChrome title={SEMHR_TOUR.heatmapTitle} subtitle={`Chart · ${SEMHR_TOUR.chartRowCount} rows`} />
            <div className="px-0.5 pb-0.5 pt-0">
              <HeatmapSvg showLegend className="w-full" />
              <span className="inline-flex items-center gap-0.5 px-0.5 text-[7px] font-medium leading-none text-brand-600">
                Expand <ArrowTopRightOnSquareIcon className="h-2 w-2 shrink-0" />
              </span>
            </div>
          </div>
        )}

        {showEditSection && !showLine && (
          <div className="mt-1.5 space-y-1">
            <p className="text-[7px] font-semibold uppercase tracking-wide text-ink-400">Edit visualization</p>
            <div className="flex items-center gap-1 rounded-full border border-ink-200/90 bg-white px-2 py-1">
              <p className="min-w-0 flex-1 text-[7px] text-ink-700">{SEMHR_TOUR.editPrompt}</p>
              <QuerySubmitButton state={resolveEditSubmitState(phase)} />
            </div>
            <p className="text-[7px] font-semibold uppercase tracking-wide text-ink-400">Suggested edits</p>
            <div className="flex flex-wrap gap-1">
              {SEMHR_TOUR.editSuggestions.map((s) => (
                <span key={s} className="rounded-full border border-ink-200/90 bg-white px-1.5 py-px text-[7px] text-ink-600">{s}</span>
              ))}
            </div>
          </div>
        )}

        {showLine && (
          <div className="mt-1 space-y-1">
            <div className="overflow-hidden rounded-lg border border-ink-200/90 bg-white shadow-sm">
              <ChartCardChrome title={SEMHR_TOUR.lineChartTitle} subtitle={`Chart · ${SEMHR_TOUR.chartRowCount} rows`} />
              <div className="px-0.5 pb-0.5 pt-0">
                <AttritionLineChartSvg />
                <span className="inline-flex items-center gap-0.5 px-0.5 text-[7px] font-medium leading-none text-brand-600">
                  Expand <ArrowTopRightOnSquareIcon className="h-2 w-2 shrink-0" />
                </span>
              </div>
            </div>
            <p className="text-[7px] font-semibold uppercase tracking-wide text-ink-400">Edit visualization</p>
            <div className="flex items-center gap-1 rounded-full border border-ink-200/90 bg-white px-2 py-1">
              <p className="min-w-0 flex-1 text-[7px] text-ink-400">{SEMHR_TOUR.editPlaceholder}</p>
              <QuerySubmitButton state={resolveEditSubmitState(phase)} />
            </div>
            <div className="flex flex-wrap gap-1">
              {SEMHR_TOUR.editSuggestions.map((s) => (
                <span key={s} className="rounded-full border border-ink-200/90 bg-white px-1.5 py-px text-[7px] text-ink-600">{s}</span>
              ))}
            </div>
            <p className="rounded border border-brand-100 bg-brand-50/80 px-2 py-1 text-[7px] font-medium text-brand-700">
              Updated visualization ({SEMHR_TOUR.chartRowCount} rows).
            </p>
            <p className="text-[7px] text-ink-500">Chart type set to Line</p>
          </div>
        )}
      </div>
    </div>
  );
}
